import React, { useEffect, useMemo, useState } from "react";
import { Circle, CircleMarker, LayerGroup, LayersControl, MapContainer, Marker, Polyline, Popup, TileLayer, useMap } from "react-leaflet";
import L from "leaflet";
import { Box, FormControlLabel, Stack, Switch, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";

// Capas del mapa. "Terreno" va por defecto: el relieve y el sombreado de bosque/selva ayudan a ver si el último punto
// está en una ciudad o en una zona donde es normal perder la señal celular. Todas sin clave de API.
const CAPAS = [
  {
    nombre: "Terreno",
    url: "https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png",
    maxZoom: 17,
    atribucion: 'Mapa &copy; <a href="https://opentopomap.org">OpenTopoMap</a> (CC-BY-SA), datos &copy; OpenStreetMap',
  },
  {
    nombre: "Satélite",
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    maxZoom: 19,
    atribucion: "Imágenes &copy; Esri",
  },
  {
    nombre: "Híbrido",
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    // Nombres de lugares y límites encima de la imagen satelital.
    etiquetas: "https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}",
    maxZoom: 19,
    atribucion: "Imágenes y etiquetas &copy; Esri",
  },
  {
    nombre: "Calles",
    url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    maxZoom: 19,
    atribucion: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
  },
];

const fechaHora = (ts) => new Date(Number(ts) * 1000).toLocaleString("es-BO", {
  timeZone: "America/La_Paz", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit",
});
const distanciaKm = (a, b) => {
  const r = Math.PI / 180;
  const h = Math.sin(((b.latitud - a.latitud) * r) / 2) ** 2
    + Math.cos(a.latitud * r) * Math.cos(b.latitud * r) * Math.sin(((b.longitud - a.longitud) * r) / 2) ** 2;
  return 12742 * Math.asin(Math.sqrt(h));
};
const duracion = (seg) => {
  const min = Math.round(seg / 60);
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  return h < 48 ? `${h} h ${min % 60} min` : `${Math.floor(h / 24)} días`;
};

const estiloContenedor = {
  border: 1,
  borderColor: "divider",
  "& .leaflet-container": { height: "100%", width: "100%", fontFamily: "inherit" },
  // Los estilos globales de inputs deforman los radios del selector de capas: se restablecen solo dentro del mapa.
  "& .leaflet-control-layers-expanded": { px: 1.25, py: 0.75, color: "#333", bgcolor: "#fff" },
  "& .leaflet-control-layers-base label": { display: "block", m: 0, cursor: "pointer" },
  "& .leaflet-control-layers-base label > span, & .leaflet-control-layers-base label > div": {
    display: "flex", alignItems: "center", gap: "6px", py: "3px", fontSize: 12.5, whiteSpace: "nowrap",
  },
  "& .leaflet-control-layers-selector": {
    appearance: "auto", display: "inline-block", position: "static", width: 14, height: 14, m: 0, p: 0, flexShrink: 0, opacity: 1,
  },
};

const SelectorCapas = () => (
  <LayersControl position="topright">
    {CAPAS.map((c, i) => (
      <LayersControl.BaseLayer key={c.nombre} name={c.nombre} checked={i === 0}>
        {c.etiquetas ? (
          // Una sola opción del control: imagen satelital + nombres de lugares encima.
          <LayerGroup>
            <TileLayer url={c.url} attribution={c.atribucion} maxZoom={c.maxZoom} maxNativeZoom={c.maxZoom} />
            <TileLayer url={c.etiquetas} maxZoom={c.maxZoom} maxNativeZoom={c.maxZoom} />
          </LayerGroup>
        ) : (
          <TileLayer url={c.url} attribution={c.atribucion} maxZoom={c.maxZoom} maxNativeZoom={c.maxZoom} />
        )}
      </LayersControl.BaseLayer>
    ))}
  </LayersControl>
);

// Mapa de un solo punto (última posición de una plataforma), con las mismas capas que el recorrido.
export function MapaUbicacion({ latitud, longitud, color = "#2a78d6", titulo, height = 300 }) {
  const punto = useMemo(() => [{ latitud, longitud }], [latitud, longitud]);
  return (
    <Box sx={{ height, ...estiloContenedor, border: 0 }}>
      <MapContainer center={[latitud, longitud]} zoom={15} scrollWheelZoom>
        <AjustarVista puntos={punto} />
        <SelectorCapas />
        <CircleMarker center={[latitud, longitud]} radius={9} pathOptions={{ color: "#fff", fillColor: color, fillOpacity: 1, weight: 3 }}>
          {titulo && <Popup><Box sx={{ fontSize: 12 }}><b>{titulo}</b><br />{latitud.toFixed(6)}, {longitud.toFixed(6)}</Box></Popup>}
        </CircleMarker>
      </MapContainer>
    </Box>
  );
}

// Cuando los mensajes cargados no traen posición: la última posición GPS válida (puede ser de días atrás) y la ubicación
// aproximada del chip según Emnify (por antena celular, con su radio de precisión), unidas por una línea punteada.
export function MapaPosiciones({ fix = null, emnify = null, height = 340 }) {
  const theme = useTheme();
  const colorFix = theme.palette.mode === "dark" ? "#3987e5" : "#2a78d6";
  const colorEmnify = theme.palette.mode === "dark" ? "#d95926" : "#eb6834";
  const puntos = useMemo(() => [fix, emnify].filter(Boolean), [fix, emnify]);
  if (!puntos.length) return null;
  const km = fix && emnify ? distanciaKm(fix, emnify) : null;
  const leyenda = (color, texto) => (
    <Stack direction="row" spacing={0.6} alignItems="center">
      <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: color, border: "2px solid #fff", boxShadow: 1 }} />
      <Typography sx={{ fontSize: 11.5 }}>{texto}</Typography>
    </Stack>
  );
  return (
    <Stack spacing={0.75}>
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
        {fix && leyenda(colorFix, `Última posición GPS válida · ${fechaHora(fix.ts)}`)}
        {emnify && leyenda(colorEmnify, `Emnify (antena celular, ~${emnify.precision_m ?? "?"} m)${emnify.ts ? ` · ${fechaHora(emnify.ts)}` : ""}`)}
        {km !== null && <Typography sx={{ fontSize: 11.5, fontWeight: 800 }}>~{km.toFixed(km < 10 ? 1 : 0)} km entre ambas</Typography>}
      </Stack>
      <Box sx={{ height, ...estiloContenedor }}>
        <MapContainer center={[puntos[0].latitud, puntos[0].longitud]} zoom={13} scrollWheelZoom>
          <AjustarVista puntos={puntos} />
          <SelectorCapas />
          {fix && emnify && (
            <Polyline positions={[[fix.latitud, fix.longitud], [emnify.latitud, emnify.longitud]]} pathOptions={{ color: colorEmnify, weight: 2, dashArray: "6 8", opacity: 0.8 }} />
          )}
          {emnify?.precision_m && (
            <Circle center={[emnify.latitud, emnify.longitud]} radius={emnify.precision_m} pathOptions={{ color: colorEmnify, weight: 1, fillOpacity: 0.12 }} />
          )}
          {emnify && (
            <CircleMarker center={[emnify.latitud, emnify.longitud]} radius={9} pathOptions={{ color: "#fff", fillColor: colorEmnify, fillOpacity: 1, weight: 3 }}>
              <Popup><DetallePunto p={emnify} titulo={`Emnify: ubicación por antena celular (~${emnify.precision_m ?? "?"} m)`} /></Popup>
            </CircleMarker>
          )}
          {fix && (
            <CircleMarker center={[fix.latitud, fix.longitud]} radius={9} pathOptions={{ color: "#fff", fillColor: colorFix, fillOpacity: 1, weight: 3 }}>
              <Popup><DetallePunto p={fix} titulo="Última posición GPS válida" /></Popup>
            </CircleMarker>
          )}
        </MapContainer>
      </Box>
    </Stack>
  );
}

function AjustarVista({ puntos }) {
  const map = useMap();
  useEffect(() => {
    // El mapa se monta dentro de una pestaña del diálogo: recalcula el tamaño antes de encuadrar.
    map.invalidateSize();
    if (puntos.length === 1) map.setView([puntos[0].latitud, puntos[0].longitud], 15, { animate: false });
    else if (puntos.length > 1) map.fitBounds(puntos.map((p) => [p.latitud, p.longitud]), { padding: [30, 30], maxZoom: 16, animate: false });
  }, [map, puntos]);
  return null;
}

const DetallePunto = ({ p, titulo }) => (
  <Box sx={{ fontSize: 12, lineHeight: 1.5 }}>
    {titulo && <b>{titulo}<br /></b>}
    {p.ts ? <>{fechaHora(p.ts)}<br /></> : null}
    {p.velocidad !== null && p.velocidad !== undefined && <>Velocidad: {Math.round(p.velocidad)} km/h<br /></>}
    {p.voltaje !== null && p.voltaje !== undefined && <>Alimentación: {Math.round(p.voltaje * 100) / 100} V<br /></>}
    {p.senal !== null && p.senal !== undefined && <>Señal: {Math.round(p.senal)} %<br /></>}
    {p.ignicion !== null && p.ignicion !== undefined && <>Ignición: {p.ignicion ? "ON" : "OFF"}<br /></>}
    <a href={`https://www.google.com/maps?q=${p.latitud},${p.longitud}`} target="_blank" rel="noreferrer">Abrir en Google Maps</a>
  </Box>
);

// Recorrido a partir de los mensajes cargados en el diagnóstico (los más recientes primero). Los tramos entre dos mensajes
// separados por más que el umbral de hueco se dibujan punteados: ahí el equipo no reportó.
export default function MapaRecorridoDiagnostico({ mensajes = [], icono = null, umbralHueco = 900 }) {
  const theme = useTheme();
  const [verPuntos, setVerPuntos] = useState(false);
  const colorRuta = theme.palette.mode === "dark" ? "#3987e5" : "#2a78d6";
  const colorHueco = theme.palette.mode === "dark" ? "#d95926" : "#eb6834";

  // Solo posiciones válidas, en orden cronológico. Los mensajes sin GPS repiten la última posición conocida: se omiten.
  const puntos = useMemo(() => mensajes
    .filter((m) => m.latitud !== null && m.longitud !== null && (m.latitud || m.longitud) && m.gps_valido !== false)
    .sort((a, b) => a.ts - b.ts), [mensajes]);

  const { tramos, huecos, km } = useMemo(() => {
    const t = [];
    const h = [];
    let actual = [];
    let total = 0;
    puntos.forEach((p, i) => {
      if (i > 0) {
        const prev = puntos[i - 1];
        if (p.ts - prev.ts > umbralHueco) {
          if (actual.length) t.push(actual);
          h.push({ desde: prev, hasta: p });
          actual = [];
        } else {
          total += distanciaKm(prev, p);
        }
      }
      actual.push([p.latitud, p.longitud]);
    });
    if (actual.length) t.push(actual);
    return { tramos: t, huecos: h, km: total };
  }, [puntos, umbralHueco]);

  const ultimo = puntos[puntos.length - 1];
  const primero = puntos[0];
  const iconoUnidad = useMemo(() => (icono
    ? L.icon({ iconUrl: icono, iconSize: [34, 34], iconAnchor: [17, 17], popupAnchor: [0, -16] })
    : null), [icono]);

  if (!puntos.length) {
    return <Typography sx={{ fontSize: 12, color: "text.secondary", py: 3, textAlign: "center" }}>Los mensajes cargados no tienen posiciones GPS válidas para dibujar en el mapa.</Typography>;
  }

  return (
    <Stack spacing={0.75}>
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography sx={{ fontSize: 11.5 }}>
          <b>{puntos.length}</b> posiciones · {fechaHora(primero.ts)} → {fechaHora(ultimo.ts)} · ~<b>{km.toFixed(1)} km</b> recorridos
          {huecos.length > 0 && <> · <Box component="span" sx={{ color: colorHueco, fontWeight: 700 }}>{huecos.length} tramo(s) sin reportar</Box></>}
        </Typography>
        <FormControlLabel sx={{ ml: "auto", "& .MuiFormControlLabel-label": { fontSize: 11.5 } }}
          control={<Switch size="small" checked={verPuntos} onChange={(e) => setVerPuntos(e.target.checked)} />} label="Mostrar cada punto" />
      </Stack>
      <Box sx={{ height: 380, ...estiloContenedor }}>
        <MapContainer center={[ultimo.latitud, ultimo.longitud]} zoom={14} scrollWheelZoom>
          <AjustarVista puntos={puntos} />
          <SelectorCapas />

          {tramos.map((t, i) => (
            <Polyline key={`t${i}`} positions={t} pathOptions={{ color: colorRuta, weight: 4, opacity: 0.9 }} />
          ))}
          {huecos.map((h) => (
            <Polyline key={`h${h.desde.ts}`} positions={[[h.desde.latitud, h.desde.longitud], [h.hasta.latitud, h.hasta.longitud]]}
              pathOptions={{ color: colorHueco, weight: 3, dashArray: "6 8" }}>
              <Popup><Box sx={{ fontSize: 12 }}><b>Sin reportar {duracion(h.hasta.ts - h.desde.ts)}</b><br />{fechaHora(h.desde.ts)} → {fechaHora(h.hasta.ts)}</Box></Popup>
            </Polyline>
          ))}
          {verPuntos && puntos.slice(0, -1).map((p, i) => (
            <CircleMarker key={`p${p.ts}-${i}`} center={[p.latitud, p.longitud]} radius={3} pathOptions={{ color: colorRuta, fillOpacity: 1, weight: 1 }}>
              <Popup><DetallePunto p={p} /></Popup>
            </CircleMarker>
          ))}
          {puntos.length > 1 && (
            <CircleMarker center={[primero.latitud, primero.longitud]} radius={6} pathOptions={{ color: "#fff", fillColor: "#1baf7a", fillOpacity: 1, weight: 2 }}>
              <Popup><DetallePunto p={primero} titulo="Inicio del recorrido cargado" /></Popup>
            </CircleMarker>
          )}
          {iconoUnidad ? (
            <Marker position={[ultimo.latitud, ultimo.longitud]} icon={iconoUnidad} zIndexOffset={1000}>
              <Popup><DetallePunto p={ultimo} titulo="Última posición" /></Popup>
            </Marker>
          ) : (
            <CircleMarker center={[ultimo.latitud, ultimo.longitud]} radius={8} pathOptions={{ color: "#fff", fillColor: colorRuta, fillOpacity: 1, weight: 3 }}>
              <Popup><DetallePunto p={ultimo} titulo="Última posición" /></Popup>
            </CircleMarker>
          )}
        </MapContainer>
      </Box>
      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ fontSize: 11, color: "text.secondary" }}>
        <Stack direction="row" spacing={0.5} alignItems="center"><Box sx={{ width: 18, height: 4, bgcolor: colorRuta }} /><span>Recorrido</span></Stack>
        <Stack direction="row" spacing={0.5} alignItems="center"><Box sx={{ width: 18, borderTop: `3px dashed ${colorHueco}` }} /><span>Tramo sin reportar (se unen los puntos extremos)</span></Stack>
        <Stack direction="row" spacing={0.5} alignItems="center"><Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: "#1baf7a" }} /><span>Inicio</span></Stack>
        <span>Capas: arriba a la derecha (Terreno, Satélite, Híbrido, Calles).</span>
      </Stack>
    </Stack>
  );
}

